import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useToc } from '@/components/guide/TocContext';

interface Props {
  id: string;
  title: string;
  children: ReactNode;
}

export function Section({ id, title, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const { register, unregister, setActive, scrollRef } = useToc();

  useEffect(() => {
    register({ id, title });
    return () => unregister(id);
  }, [id, title, register, unregister]);

  useEffect(() => {
    const element = ref.current;
    const root = scrollRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) setActive(id);
      },
      // Use the scroll container as root so margins are relative to the
      // overlay pane, not the viewport.  The active zone is the top 40%
      // of the pane — a section becomes active when its top edge enters
      // that band.
      { root: root ?? undefined, rootMargin: '0px 0px -60% 0px' },
    );
    observer.observe(element);

    // Scrolled-to-bottom detection: when the user scrolls to the very
    // end of the content, activate the last section regardless of where
    // its top edge sits relative to the rootMargin band.
    const scrollContainer = root;
    if (!scrollContainer) return () => observer.disconnect();

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollHeight - scrollTop - clientHeight < 2) {
        // At bottom — find the last section in the DOM order and activate it.
        const sections = scrollContainer.querySelectorAll('.guide-section');
        const last = sections[sections.length - 1];
        if (last) setActive(last.id);
      }
    };
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [id, setActive, scrollRef]);

  return (
    <div ref={ref} id={id} className="guide-section scroll-mt-12">
      <h4 className="mt-6 mb-2 text-base font-semibold text-guide-text">{title}</h4>
      <div>{children}</div>
    </div>
  );
}
